import React, { useEffect, useRef, useState } from "react";
import { Chart, registerables } from "chart.js";
import { useNavigate } from "react-router-dom";
import { ShippingData } from "../../types";

interface Stat {
    icon: string;
    link: string;
    label: string;
    bgColor: string;
    key: string;
    odooLink?: string;
    redOdooLink?: string;
    yellowOdooLink?: string;
    greenOdooLink?: string;
}

interface DashboardProps {
    startDate: string;
    endDate: string;
    dashboardData: any;
    delayedList: ShippingData[];
}

Chart.register(...registerables);

const LogisticsDashboard = (props: DashboardProps) => {
    const navigate = useNavigate();

    const [rfqCount, setRfqCount] = useState(0);
    const [poCount, setPoCount] = useState(0);
    const [arrivalCount, setArrivalCount] = useState(0);
    const [delayedCount, setDelayedCount] = useState(0);
    const [alertCount, setAlertCount] = useState(0);
    const [notReceivedCount, setNotReceivedCount] = useState(0);

    const barChartRef = useRef<HTMLCanvasElement>(null);
    const pieChartRef = useRef<HTMLCanvasElement>(null);
    const barChartInstance = useRef<Chart | null>(null);
    const pieChartInstance = useRef<Chart | null>(null);
    const [chartData, setChartData] = useState([0, 0, 0, 0]);

    const [barChartData, setBarChartData] = useState([0, 0, 0, 0, 0, 0, 0, 0]);
    const [dropShiopBarchartData, setDropshipBarchartData] = useState([
        0, 0, 0, 0, 0, 0, 0, 0,
    ]);
    const [inventoryBarchartData, setInventoryBarchartData] = useState([
        0, 0, 0, 0, 0, 0, 0, 0,
    ]);
    const [delayedBarChartData, setDelayedBarChartData] = useState([
        0, 0, 0, 0, 0, 0, 0, 0,
    ]);

    function handleGoToNextStepAction(id: number) {
        window.open(
            `https://odoo-ps-psae-carandx.odoo.com/web#id=${id}&cids=3&menu_id=309&action=470&model=purchase.order&view_type=form`,
            "_blank",
            "noopener,noreferrer",
        );
    }

    function getLabels() {
        // Get month names for labels
        const monthNames = [
            "Jan",
            "Feb",
            "Mar",
            "Apr",
            "May",
            "Jun",
            "Jul",
            "Aug",
            "Sep",
            "Oct",
            "Nov",
            "Dec",
        ];

        // Get current and previous month names
        const currentMonthIndex = new Date().getMonth(); // 0-indexed
        const currentMonthName = monthNames[currentMonthIndex];

        // Calculate previous month
        let previousMonthIndex = currentMonthIndex - 1;
        if (previousMonthIndex < 0) {
            previousMonthIndex = 11; // December
        }
        const previousMonthName = monthNames[previousMonthIndex];

        // Generate dynamic labels
        const labels = [
            `${previousMonthName} W1`,
            `${previousMonthName} W2`,
            `${previousMonthName} W3`,
            `${previousMonthName} W4`,
            `${currentMonthName} W1`,
            `${currentMonthName} W2`,
            `${currentMonthName} W3`,
            `${currentMonthName} W4`,
        ];

        return labels;
    }

    useEffect(() => {
        // Vertical Line Chart
        if (barChartRef.current) {
            const barCtx = barChartRef.current.getContext("2d");
            if (barCtx) {
                if (barChartInstance.current) {
                    barChartInstance.current.destroy();
                }

                barChartInstance.current = new Chart(barCtx, {
                    type: "line",
                    data: {
                        labels: getLabels(),
                        datasets: [
                            {
                                label: "Total Amount",
                                data: barChartData,
                                borderColor: "rgb(59, 130, 246)", // Blue color
                                backgroundColor: "rgba(59, 130, 246, 0.1)",
                                borderWidth: 2,
                                tension: 0.1, // Slight curve for the line
                                fill: true, // Fill area under the line
                                pointBackgroundColor: "rgb(59, 130, 246)",
                                pointBorderColor: "#fff",
                                pointBorderWidth: 2,
                                pointRadius: 6,
                                pointHoverRadius: 8,
                            },
                            {
                                label: "Dropship",
                                data: dropShiopBarchartData,
                                borderColor: "rgba(246, 143, 59, 1)", // Blue color
                                backgroundColor: "rgba(59, 130, 246, 0.1)",
                                borderWidth: 2,
                                tension: 0.1, // Slight curve for the line
                                fill: true, // Fill area under the line
                                pointBackgroundColor: "rgba(212, 90, 9, 1)",
                                pointBorderColor: "#ffffffff",
                                pointBorderWidth: 2,
                                pointRadius: 6,
                                pointHoverRadius: 8,
                            },
                            {
                                label: "Inventory",
                                data: inventoryBarchartData,
                                borderColor: "rgba(0, 151, 13, 1)", // Blue color
                                backgroundColor: "rgba(59, 130, 246, 0.1)",
                                borderWidth: 2,
                                tension: 0.1, // Slight curve for the line
                                fill: true, // Fill area under the line
                                pointBackgroundColor: "rgba(0, 100, 30, 1)",
                                pointBorderColor: "#ffffffff",
                                pointBorderWidth: 2,
                                pointRadius: 6,
                                pointHoverRadius: 8,
                            },
                            {
                                label: "Delayed",
                                data: delayedBarChartData,
                                borderColor: "rgba(165, 17, 17, 1)", // Blue color
                                backgroundColor: "rgba(59, 130, 246, 0.1)",
                                borderWidth: 2,
                                tension: 0.1, // Slight curve for the line
                                fill: true, // Fill area under the line
                                pointBackgroundColor: "rgba(104, 5, 5, 1)",
                                pointBorderColor: "#ffffffff",
                                pointBorderWidth: 2,
                                pointRadius: 6,
                                pointHoverRadius: 8,
                            },
                        ],
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: {
                                display: true,
                                position: "top",
                                labels: {
                                    usePointStyle: true,
                                    padding: 20,
                                },
                            },
                            tooltip: {
                                mode: "index",
                                intersect: false,
                                backgroundColor: "rgba(0, 0, 0, 0.7)",
                                titleColor: "#fff",
                                bodyColor: "#fff",
                                padding: 10,
                                cornerRadius: 4,
                            },
                        },
                        scales: {
                            x: {
                                grid: {
                                    display: true,
                                    color: "rgba(0, 0, 0, 0.1)",
                                },
                                ticks: {
                                    font: {
                                        size: 12,
                                        weight: "bold",
                                    },
                                    color: "#666",
                                },
                                title: {
                                    display: true,
                                    text: "Weeks",
                                    color: "#666",
                                    font: {
                                        size: 14,
                                        weight: "bold",
                                    },
                                },
                            },
                            y: {
                                grid: {
                                    display: true,
                                    color: "rgba(0, 0, 0, 0.1)",
                                },
                                ticks: {
                                    font: {
                                        size: 12,
                                    },
                                    color: "#666",
                                    callback: function (value) {
                                        // Format as currency if needed
                                        return "AED " + value.toLocaleString();
                                    },
                                },
                                title: {
                                    display: true,
                                    text: "Total Amount",
                                    color: "#666",
                                    font: {
                                        size: 14,
                                        weight: "bold",
                                    },
                                },
                            },
                        },
                        interaction: {
                            intersect: false,
                            mode: "nearest",
                        },
                        elements: {
                            line: {
                                tension: 0.3, // Smooth curves
                            },
                        },
                    },
                });
            }
        }

        // Pie Chart
        if (pieChartRef.current) {
            const pieCtx = pieChartRef.current.getContext("2d");
            if (pieCtx) {
                if (pieChartInstance.current) {
                    pieChartInstance.current.destroy();
                }

                pieChartInstance.current = new Chart(pieCtx, {
                    type: "pie",
                    data: {
                        labels: [
                            "To Be Shipped Amount",
                            "Transit Amount",
                            "On Hand Amount",
                        ],
                        datasets: [
                            {
                                data: chartData,
                                backgroundColor: [
                                    "#5bd8aeff",
                                    "rgba(20, 164, 167, 1)",
                                    "#0d9e12ff",
                                ],
                                borderWidth: 2,
                                borderColor: "#fff",
                            },
                        ],
                    },
                    options: {
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                            legend: {
                                position: "right",
                                labels: {
                                    usePointStyle: true,
                                    padding: 15,
                                    font: {
                                        size: 11,
                                    },
                                },
                            },
                        },
                    },
                });
            }
        }

        return () => {
            if (barChartInstance.current) {
                barChartInstance.current.destroy();
            }
            if (pieChartInstance.current) {
                pieChartInstance.current.destroy();
            }
        };
    }, [chartData]);

    useEffect(() => {
        if (props.dashboardData) {
            setChartData([
                props.dashboardData?.overview.rfq.totalAmount,
                props.dashboardData?.overview.inTransit.totalAmount,
                props.dashboardData?.overview.delivered.totalAmount,
            ]);
        }

        setBarChartData([
            props.dashboardData.quarter?.overview.q1.totalAmount,
            props.dashboardData.quarter?.overview.q2.totalAmount,
            props.dashboardData.quarter?.overview.q3.totalAmount,
            props.dashboardData.quarter?.overview.q4.totalAmount,
            props.dashboardData.quarter?.overview.q5.totalAmount,
            props.dashboardData.quarter?.overview.q6.totalAmount,
            props.dashboardData.quarter?.overview.q7.totalAmount,
            props.dashboardData.quarter?.overview.q8.totalAmount,
        ]);
        setDropshipBarchartData([
            props.dashboardData.quarter.dropship.q1.totalAmount,
            props.dashboardData.quarter.dropship.q2.totalAmount,
            props.dashboardData.quarter.dropship.q3.totalAmount,
            props.dashboardData.quarter.dropship.q4.totalAmount,
            props.dashboardData.quarter.dropship.q5.totalAmount,
            props.dashboardData.quarter.dropship.q6.totalAmount,
            props.dashboardData.quarter.dropship.q7.totalAmount,
            props.dashboardData.quarter.dropship.q8.totalAmount,
        ]);
        setInventoryBarchartData([
            props.dashboardData.quarter.inventory.q1.totalAmount,
            props.dashboardData.quarter.inventory.q2.totalAmount,
            props.dashboardData.quarter.inventory.q3.totalAmount,
            props.dashboardData.quarter.inventory.q4.totalAmount,
            props.dashboardData.quarter.inventory.q5.totalAmount,
            props.dashboardData.quarter.inventory.q6.totalAmount,
            props.dashboardData.quarter.inventory.q7.totalAmount,
            props.dashboardData.quarter.inventory.q8.totalAmount,
        ]);
        setDelayedBarChartData([
            props.dashboardData.quarter.delayed.q1.totalAmount,
            props.dashboardData.quarter.delayed.q2.totalAmount,
            props.dashboardData.quarter.delayed.q3.totalAmount,
            props.dashboardData.quarter.delayed.q4.totalAmount,
            props.dashboardData.quarter.delayed.q5.totalAmount,
            props.dashboardData.quarter.delayed.q6.totalAmount,
            props.dashboardData.quarter.delayed.q7.totalAmount,
            props.dashboardData.quarter.delayed.q8.totalAmount,
        ]);
    }, [props.dashboardData]);

    const overviewStats: Stat[] = [
        {
            icon: "/icons/dashboard/shipping.png",
            link: "/rfq-list",
            label: "To Be Shipped",
            bgColor: "#E0F2F1",
            key: "rfq",
        },
        {
            icon: "/icons/dashboard/transit.png",
            link: "/po-list",
            label: "In Transit",
            bgColor: "#E3F2FD",
            key: "inTransit",
        },
        {
            icon: "/icons/dashboard/delivered.png",
            link: "/arrival-list",
            label: "On Hand",
            bgColor: "#E8F5E9",
            key: "delivered",
        },
    ];

    const dropshipStats: Stat[] = [
        {
            icon: "/icons/dashboard/shipping.png",
            link: "/rfq-list",
            label: "Sold Car Not Ship",
            bgColor: "#E0F2F1",
            key: "rfq",
            odooLink:
                "https://odoo-ps-psae-carandx.odoo.com/web#action=1655&model=purchase.order&view_type=list",
            redOdooLink:
                "https://odoo-ps-psae-carandx.odoo.com/web#action=1679&model=purchase.order&view_type=list",
            yellowOdooLink:
                "https://odoo-ps-psae-carandx.odoo.com/web#action=1678&model=purchase.order&view_type=list",
            greenOdooLink:
                "https://odoo-ps-psae-carandx.odoo.com/web#action=1677&model=purchase.order&view_type=list",
        },
        {
            icon: "/icons/dashboard/transit.png",
            link: "/po-list",
            label: "Sold Car In Sea",
            bgColor: "#E3F2FD",
            key: "inTransit",
            odooLink:
                "https://odoo-ps-psae-carandx.odoo.com/web#action=1652&model=purchase.order&view_type=list",
            redOdooLink:
                "https://odoo-ps-psae-carandx.odoo.com/web#action=1673&model=purchase.order&view_type=list",
            yellowOdooLink:
                "https://odoo-ps-psae-carandx.odoo.com/web#action=1672&model=purchase.order&view_type=list",
            greenOdooLink:
                "https://odoo-ps-psae-carandx.odoo.com/web#action=1671&model=purchase.order&view_type=list",
        },
    ];

    const inventoryStats: Stat[] = [
        {
            icon: "/icons/dashboard/shipping.png",
            link: "/rfq-list",
            label: "Inventory Not Ship",
            bgColor: "#E0F2F1",
            key: "rfq",
            odooLink:
                "https://odoo-ps-psae-carandx.odoo.com/web#action=1656&model=purchase.order&view_type=list",
            redOdooLink:
                "https://odoo-ps-psae-carandx.odoo.com/web#action=1682&model=purchase.order&view_type=list",
            yellowOdooLink:
                "https://odoo-ps-psae-carandx.odoo.com/web#action=1681&model=purchase.order&view_type=list",
            greenOdooLink:
                "https://odoo-ps-psae-carandx.odoo.com/web#action=1680&model=purchase.order&view_type=list",
        },
        {
            icon: "/icons/dashboard/transit.png",
            link: "/po-list",
            label: "Inventory In Sea",
            bgColor: "#E3F2FD",
            key: "inTransit",
            odooLink:
                "https://odoo-ps-psae-carandx.odoo.com/web#action=1653&model=purchase.order&view_type=list",
            redOdooLink:
                "https://odoo-ps-psae-carandx.odoo.com/web#action=1676&model=purchase.order&view_type=list",
            yellowOdooLink:
                "https://odoo-ps-psae-carandx.odoo.com/web#action=1675&model=purchase.order&view_type=list",
            greenOdooLink:
                "https://odoo-ps-psae-carandx.odoo.com/web#action=1674&model=purchase.order&view_type=list",
        },
    ];

    const shipmentStages = [
        { stage: "PO Created", count: 130 },
        { stage: "LDS Confirmed", count: 130 },
        { stage: "ETD Confirmed", count: 130 },
        { stage: "BL Issued", count: 130 },
        { stage: "ETA Confirmed", count: 130 },
        { stage: "ORG BL Received", count: 130 },
        { stage: "REO DO Submitted", count: 130 },
        { stage: "Inspected", count: 130 },
    ];

    function isDateOlderThanToday(dateString: string): boolean {
        const inputDate = new Date(dateString);
        const today = new Date();

        inputDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);

        return inputDate < today;
    }

    function isDateInTwoDays(dateString: string): boolean {
        const inputDate = new Date(dateString);
        const today = new Date();
        const twoDaysFromNow = new Date();

        inputDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        twoDaysFromNow.setHours(0, 0, 0, 0);
        twoDaysFromNow.setDate(today.getDate() + 2);

        return inputDate.getTime() === twoDaysFromNow.getTime();
    }

    function getNextStepColor(date: string | undefined) {
        if (!date) {
            return "#fff";
        }

        if (isDateOlderThanToday(date)) {
            return "#fa7f52ff";
        } else if (isDateInTwoDays(date)) {
            return "#e0f052ff";
        } else {
            return "#43bd38ff";
        }
    }

    function truncateText(text: string | null, maxLength: number = 20): string {
        if (!text) return "";
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + "...";
    }

    const getStatValue = function (index: number) {
        switch (index) {
            case 0:
                return rfqCount;
            case 1:
                return poCount;
            case 2:
                return arrivalCount;
            case 3:
                return delayedCount;
            case 4:
                return alertCount;
            default:
                return notReceivedCount;
        }
    };

    function compareDateToToday(inputDateStr: string | undefined) {
        if (!inputDateStr) return "";

        // Normalize input: handle both "YYYY-MM-DD" and "YYYY-MM-DD HH:mm:ss"
        const normalized =
            inputDateStr.length === 10
                ? inputDateStr + " 00:00:00"
                : inputDateStr;

        const inputDate = new Date(normalized.replace(" ", "T"));
        const today = new Date();

        // Normalize today's time to midnight
        today.setHours(0, 0, 0, 0);

        // Calculate difference in days
        const diffMs = inputDate.getTime() - today.getTime();
        const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

        // Format with + or -
        if (diffDays === 0) return "0 days";

        const sign = diffDays > 0 ? "+" : "-";
        return `${sign}${Math.abs(diffDays)} day${
            Math.abs(diffDays) > 1 ? "s" : ""
        }`;
    }

    return (
        <div
            style={{
                fontFamily:
                    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                backgroundColor: "#f8f9fa",
                padding: "20px",
            }}
        >
            {/* Main Content */}
            <div className="row h-100" style={{ backgroundColor: "#fff" }}>
                {/* Pie Chart */}
                <div className="col-lg-8">
                    <div className="card border-0 h-100">
                        <div className="card-body p-3">
                            <h6
                                className="mb-3"
                                style={{
                                    fontSize: "14px",
                                    fontWeight: "600",
                                    color: "#2c3e50",
                                }}
                            >
                                Shipment Distribution
                            </h6>
                            <div
                                style={{
                                    height: "120px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <canvas ref={pieChartRef}></canvas>
                            </div>
                            <div className="mt-3 pt-3 border-top">
                                <div className="row text-center">
                                    <div className="col-5 text-start">
                                        <div
                                            style={{
                                                fontSize: "11px",
                                                color: "#78909c",
                                                marginBottom: "4px",
                                            }}
                                        >
                                            To Be Shipped
                                        </div>
                                        <div
                                            style={{
                                                fontSize: "12px",
                                                fontWeight: "600",
                                                color: "#2c3e50",
                                            }}
                                        >
                                            <p className="fw-bold mb-0">
                                                Total:{" "}
                                                {props.dashboardData?.overview.rfq?.totalCount?.toLocaleString()}{" "}
                                                | AED{" "}
                                                {props.dashboardData?.overview.rfq?.totalAmount?.toLocaleString()}
                                                | PO:
                                                {
                                                    props.dashboardData
                                                        ?.overview.rfq?.totalPo
                                                }
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-5 text-start">
                                        <div
                                            style={{
                                                fontSize: "12px",
                                                color: "#78909c",
                                                marginBottom: "4px",
                                            }}
                                        >
                                            In Transit
                                        </div>
                                        <div
                                            style={{
                                                fontSize: "12px",
                                                fontWeight: "600",
                                                color: "#2c3e50",
                                            }}
                                        >
                                            <p className="fw-bold mb-0">
                                                Total:{" "}
                                                {props.dashboardData?.overview.inTransit?.totalCount?.toLocaleString()}{" "}
                                                | AED{" "}
                                                {props.dashboardData?.overview.inTransit?.totalAmount?.toLocaleString()}
                                                | PO:
                                                {
                                                    props.dashboardData
                                                        ?.overview.inTransit
                                                        ?.totalPo
                                                }
                                            </p>
                                        </div>
                                    </div>
                                    <div className="col-2 text-start">
                                        <div
                                            style={{
                                                fontSize: "12px",
                                                color: "#78909c",
                                                marginBottom: "4px",
                                            }}
                                        >
                                            On Hand
                                        </div>
                                        <div
                                            style={{
                                                fontSize: "12px",
                                                fontWeight: "600",
                                                color: "#2c3e50",
                                            }}
                                        >
                                            <p className="fw-bold mb-0">
                                                Total:{" "}
                                                {props.dashboardData?.overview.delivered?.totalCount?.toLocaleString()}{" "}
                                                | AED{" "}
                                                {props.dashboardData?.overview.delivered?.totalAmount?.toLocaleString()}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Pie Chart */}
                <div className="col-lg-4">
                    <div className="card border-0 h-100">
                        <div className="card-body p-3">
                            <div
                                style={{
                                    height: "220px",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                }}
                            >
                                <canvas ref={barChartRef}></canvas>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-3 mt-1">
                {dropshipStats.map((stat, index) => (
                    <div key={index} className="col-lg-6">
                        <div
                            className="card border-0 h-100 rounded-3"
                            style={{
                                backgroundColor: stat.bgColor,
                                boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                            }}
                        >
                            {/* Header */}
                            <div
                                className="d-flex align-items-between px-3 pt-3"
                                style={{
                                    justifyContent: "space-between",
                                }}
                            >
                                {/* LEFT: Label + Icon */}
                                <div className="d-flex align-items-center gap-2">
                                    <a href={stat.odooLink}>
                                        <h6
                                            className="fw-semibold mb-0"
                                            style={{ fontSize: "0.85rem" }}
                                        >
                                            {stat.label}
                                        </h6>
                                    </a>

                                    <img
                                        src={stat.icon}
                                        alt={stat.label}
                                        style={{ width: 16, height: 16 }}
                                    />
                                </div>
                                <a href={stat.odooLink}>
                                    <h6
                                        className="fw-semibold mb-0"
                                        style={{ fontSize: "0.85rem" }}
                                    >
                                        Total:{" "}
                                        {props.dashboardData.dropship[
                                            stat.key
                                        ]?.totalCount?.toLocaleString()}{" "}
                                        | AED{" "}
                                        {props.dashboardData.dropship[
                                            stat.key
                                        ]?.totalAmount?.toLocaleString()}{" "}
                                        | PO{" "}
                                        {props.dashboardData.dropship[
                                            stat.key
                                        ]?.totalPo?.toLocaleString()}
                                    </h6>
                                </a>
                            </div>

                            {/* Body */}
                            <div className="card-body">
                                <div className="row g-2">
                                    {/* GREEN – After today */}
                                    <a
                                        className="col-4 text-end"
                                        href={stat.greenOdooLink}
                                        target="_blank"
                                    >
                                        <div
                                            className="rounded-3 p-2"
                                            style={{
                                                background: "#BFE7D1",
                                                height: "100%",
                                            }}
                                        >
                                            <div className="fw-semibold text-dark">
                                                {props.dashboardData.dropship[
                                                    stat.key
                                                ]?.totalBefore7DaysCount?.toLocaleString()}
                                            </div>

                                            <div
                                                className="text-muted"
                                                style={{
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                AED{" "}
                                                {props.dashboardData.dropship[
                                                    stat.key
                                                ]?.totalBefore7DaysAmount?.toLocaleString()}
                                            </div>

                                            <div
                                                className="text-muted"
                                                style={{
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                PO:{" "}
                                                {props.dashboardData.dropship[
                                                    stat.key
                                                ]?.totalPoBefore7Days?.toLocaleString()}
                                            </div>
                                        </div>
                                    </a>

                                    {/* YELLOW – In 7 days */}
                                    <a
                                        className="col-4 text-end"
                                        href={stat.yellowOdooLink}
                                        target="_blank"
                                    >
                                        <div
                                            className="rounded-3 p-2"
                                            style={{
                                                background: "#FFE08A",
                                            }}
                                        >
                                            <div className="fw-semibold text-dark">
                                                {props.dashboardData.dropship[
                                                    stat.key
                                                ]?.totalIn7DaysCount?.toLocaleString()}
                                            </div>

                                            <div
                                                className="text-muted"
                                                style={{
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                AED{" "}
                                                {props.dashboardData.dropship[
                                                    stat.key
                                                ]?.totalIn7DaysAmount?.toLocaleString()}
                                            </div>

                                            <div
                                                className="text-muted"
                                                style={{
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                PO in 7 days:{" "}
                                                {props.dashboardData.dropship[
                                                    stat.key
                                                ]?.totalPoIn7Days?.toLocaleString()}
                                            </div>
                                        </div>
                                    </a>

                                    {/* RED – Before today */}
                                    <a
                                        className="col-4 text-end"
                                        href={stat.redOdooLink}
                                        target="_blank"
                                    >
                                        <div
                                            className="rounded-3 p-2"
                                            style={{
                                                background: "#F4A6A6",
                                                height: "100%",
                                            }}
                                        >
                                            <div className="fw-semibold text-dark">
                                                {props.dashboardData.dropship[
                                                    stat.key
                                                ]?.totalAfterTodayCount?.toLocaleString()}
                                            </div>

                                            <div
                                                className="text-muted"
                                                style={{
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                AED{" "}
                                                {props.dashboardData.dropship[
                                                    stat.key
                                                ]?.totalAfterTodayAmount?.toLocaleString()}
                                            </div>

                                            <div
                                                className="text-muted"
                                                style={{
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                PO :{" "}
                                                {props.dashboardData.dropship[
                                                    stat.key
                                                ]?.totalPoAfterToday?.toLocaleString()}
                                            </div>
                                        </div>
                                    </a>
                                    <div className="text-end"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="row g-3 mt-1">
                {inventoryStats.map((stat, index) => (
                    <div key={index} className="col-lg-6">
                        <div
                            className="card border-0 h-100 rounded-3"
                            style={{
                                backgroundColor: stat.bgColor,
                                boxShadow: "0 6px 18px rgba(0,0,0,0.06)",
                            }}
                        >
                            {/* Header */}
                            <div
                                className="d-flex align-items-center px-3 pt-3"
                                style={{
                                    justifyContent: "space-between",
                                }}
                            >
                                {/* LEFT: Label + Icon */}
                                <div className="d-flex align-items-center gap-2">
                                    <a href={stat.odooLink} target="_blank">
                                        <h6
                                            className="fw-semibold mb-0"
                                            style={{ fontSize: "0.85rem" }}
                                        >
                                            {stat.label}
                                        </h6>
                                    </a>

                                    <img
                                        src={stat.icon}
                                        alt={stat.label}
                                        style={{ width: 16, height: 16 }}
                                    />
                                </div>
                                <a href={stat.odooLink} target="_blank">
                                    <h6
                                        className="fw-semibold mb-0"
                                        style={{ fontSize: "0.85rem" }}
                                    >
                                        Total:{" "}
                                        {props.dashboardData.inventory[
                                            stat.key
                                        ]?.totalCount?.toLocaleString()}{" "}
                                        | AED{" "}
                                        {props.dashboardData.inventory[
                                            stat.key
                                        ]?.totalAmount?.toLocaleString()}{" "}
                                        | PO{" "}
                                        {props.dashboardData.inventory[
                                            stat.key
                                        ]?.totalPo?.toLocaleString()}
                                    </h6>
                                </a>
                            </div>

                            {/* Body */}
                            <div className="card-body">
                                <div className="row g-2">
                                    {/* GREEN – After today */}
                                    <a className="col-4 text-end" href={stat.greenOdooLink} target="_blank">
                                        <div
                                            className="rounded-3 p-2"
                                            style={{
                                                background: "#BFE7D1",
                                                height: "100%",
                                            }}
                                        >
                                            <div className="fw-semibold text-dark">
                                                {props.dashboardData.inventory[
                                                    stat.key
                                                ]?.totalBefore7DaysCount?.toLocaleString()}
                                            </div>

                                            <div
                                                className="text-muted"
                                                style={{
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                AED{" "}
                                                {props.dashboardData.inventory[
                                                    stat.key
                                                ]?.totalBefore7DaysAmount?.toLocaleString()}
                                            </div>

                                            <div
                                                className="text-muted"
                                                style={{
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                PO:{" "}
                                                {props.dashboardData.inventory[
                                                    stat.key
                                                ]?.totalPoBefore7Days?.toLocaleString()}
                                            </div>
                                        </div>
                                    </a>

                                    {/* YELLOW – In 7 days */}
                                    <a className="col-4 text-end" href={stat.yellowOdooLink} target="_blank">
                                        <div
                                            className="rounded-3 p-2"
                                            style={{
                                                background: "#FFE08A",
                                            }}
                                        >
                                            <div className="fw-semibold text-dark">
                                                {props.dashboardData.inventory[
                                                    stat.key
                                                ]?.totalIn7DaysCount?.toLocaleString()}
                                            </div>

                                            <div
                                                className="text-muted"
                                                style={{
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                AED{" "}
                                                {props.dashboardData.inventory[
                                                    stat.key
                                                ]?.totalIn7DaysAmount?.toLocaleString()}
                                            </div>

                                            <div
                                                className="text-muted"
                                                style={{
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                PO in 7 days:{" "}
                                                {props.dashboardData.inventory[
                                                    stat.key
                                                ]?.totalPoIn7Days?.toLocaleString()}
                                            </div>
                                        </div>
                                    </a>

                                    {/* RED – Before today */}
                                    <a className="col-4 text-end" href={stat.redOdooLink} target="_blank">
                                        <div
                                            className="rounded-3 p-2"
                                            style={{
                                                background: "#F4A6A6",
                                                height: "100%",
                                            }}
                                        >
                                            <div className="fw-semibold text-dark">
                                                {props.dashboardData.inventory[
                                                    stat.key
                                                ]?.totalAfterTodayCount?.toLocaleString()}
                                            </div>

                                            <div
                                                className="text-muted"
                                                style={{
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                AED{" "}
                                                {props.dashboardData.inventory[
                                                    stat.key
                                                ]?.totalAfterTodayAmount?.toLocaleString()}
                                            </div>

                                            <div
                                                className="text-muted"
                                                style={{
                                                    fontSize: "0.75rem",
                                                }}
                                            >
                                                PO :{" "}
                                                {props.dashboardData.inventory[
                                                    stat.key
                                                ]?.totalPoAfterToday?.toLocaleString()}
                                            </div>
                                        </div>
                                    </a>
                                    <div className="text-end"></div>
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default LogisticsDashboard;
